import { APIProvider, InfoWindow, Map, Marker } from "@vis.gl/react-google-maps";
import { Card, CardSection, Loader } from "cxa-components";
import React, { Fragment, useEffect, useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getClinics, getECardsData, searchClinics, setSelectedEcards } from "../../actions/claimActionsPP";
import markerIconCustom from "../../assets/images/location-pin-current-transparent.gif";
import phone from "../../assets/images/phone.png";
import { ClickOutside } from "../common/ClickOutsideWrapper";
import messages from "../messagesDescription";
import { formatDistance } from "../common/FormatDistance";

// eslint-disable-next-line
const Clinics = ({ intl }) => {
  const { eCardsList, clinicsList, isFetching, searchClinicsList, selectedEcards } = useSelector(
    state => state.claimsPP
  );
  const dispatch = useDispatch();

  const [currentLocation, setCurrentLocation] = useState({
    lat: 1.284019495406719,
    lng: 103.84679549630339,
  });
  const [dragLocation, setDragLocation] = useState(null);
  const [dragTimeout, setDragTimeout] = useState(null);

  const [activeMarker, setActiveMarker] = useState(null);
  const [isDragPin, setIsDragPin] = useState(false);
  const [isPostalCode, setIsPostalCode] = useState(false);
  const [isProviderName, setIsProviderName] = useState(false);

  // filter
  const [filterTimeout, setFilterTimeout] = useState(null);
  const [address, setAddress] = useState(null);
  const [selectedClinicsByProvider, setSelectedClinicsByProvider] = useState(null);
  const [selectedAllClinics, setSelectedAllClinics] = useState(null);
  const [dataClinics, setDataClinics] = useState({
    latitude: null,
    longtitude: null,
    radius: 5000,
    count: 100,
  });

  const [isHitApi, setIsHitApi] = useState(false);

  useEffect(() => {
    if (!eCardsList.length && !clinicsList.length) {
      dispatch(getECardsData());
      if (selectedEcards) {
        dispatch(getClinics(selectedEcards)); // for filter search by clinics name
      } else {
        dispatch(getClinics());
      }

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition( /* NOSONAR */
          position => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation({ lat: latitude, lng: longitude });

            const updatedDataClinics = {
              ...dataClinics,
              latitude: latitude,
              longtitude: longitude,
            };
            setDataClinics(updatedDataClinics);
            dispatch(searchClinics(updatedDataClinics));
          },
          error => {
            console.error("Error getting location:", error);
          }
        );
      } else {
        console.error("Geolocation not supported by this browser.");
      }
    } else if (!selectedEcards) {
      const updatedDataClinicss = {
        ...dataClinics,
        latitude: currentLocation.lat,
        longtitude: currentLocation.lng,
      };
      dispatch(searchClinics(updatedDataClinicss));
      dispatch(getClinics());
    }

    const updatedDataClinics = {
      ...dataClinics,
      latitude: currentLocation.lat,
      longtitude: currentLocation.lng,
      providerType: selectedEcards
    };
    if (selectedEcards) {
      dispatch(getClinics(selectedEcards)); // for filter search by clinics name
      dispatch(searchClinics(updatedDataClinics));
    }
  }, [eCardsList.length, clinicsList.length]);

  useEffect(() => {
    if(isHitApi){
      const location = { lat: searchClinicsList.latitude, lng: searchClinicsList.longitude };
      setCurrentLocation(location);
    }
  }, [searchClinicsList])

  if (isFetching && !eCardsList.length && !clinicsList.length) return <Loader />;

  const intlAllClinics = intl.formatMessage(messages.claims.clinicsProviderFilter)
  const defaultValue = typeof intlAllClinics === "string" ? {value : "All", label : intlAllClinics} : intlAllClinics

  const uniqueProvidersMapping = eCardsList
    .filter(card => card.dataSource && card.providerId !== "AIA")
    .reduce((acc, card) => {
        if (!acc[card.providerId]) {
            acc[card.providerId] = {
                providerId: card.providerId,
                providerName: card.providerName,
            };
        }
        return acc;
  }, {});

  const uniqueProviders = Object.values(uniqueProvidersMapping);

  const allClinicsFilter = [
    defaultValue,
    ...uniqueProviders.map(provider => ({ 
        value: provider.providerId, 
        label: provider.providerName
    })),
  ];

  const clinicsByProviderTypeFilter = [
    ...clinicsList.map(item => ({
      value: item,
      label: item,
    })),
  ];

  const debounceApiCall = updatedDataClinics => {
    if (filterTimeout) clearTimeout(filterTimeout);
    setIsHitApi(true)

    const timeout = setTimeout(() => {
      dispatch(searchClinics(updatedDataClinics));
    }, 1500);

    setFilterTimeout(timeout);
  };

  const handleAddressChange = e => {
    setIsPostalCode(true);
    setIsDragPin(false);
    setIsProviderName(false);
    setAddress(e.target.value);
  };

  const handleSubmitAddress = () => {
    const updatedDataClinics = {
      ...dataClinics,
      latitude: currentLocation.lat,
      longtitude: currentLocation.lng,
      address: address,
      providerName: selectedClinicsByProvider,
    };
    setDataClinics(updatedDataClinics);
    debounceApiCall(updatedDataClinics);
  };

  const handleProviderNameChange = selectedOption => {
    setIsDragPin(false);
    setIsProviderName(true);
    const value = selectedOption.value || null;
    setSelectedClinicsByProvider(value);
    const updatedDataClinics = {
      ...dataClinics,
      address: address,
      latitude: currentLocation.lat,
      longtitude: currentLocation.lng,
      providerName: value,
    };
    setDataClinics(updatedDataClinics);
    debounceApiCall(updatedDataClinics);
  };

  const handleAllClinicsChange = selectedOption => {
    setSelectedClinicsByProvider("");
    setIsDragPin(false);

    const value = selectedOption.value !== "All" ? selectedOption.value : null;
    dispatch(getClinics(value));
    dispatch(setSelectedEcards(null))
    setSelectedAllClinics(value);
    const updatedDataClinics = {
      ...dataClinics,
      providerType: value,
      address: address,
      providerName: selectedClinicsByProvider,
      latitude: currentLocation.lat,
      longtitude: currentLocation.lng,
    };
    setDataClinics(updatedDataClinics);
    debounceApiCall(updatedDataClinics);
  };

  const handleMapDrag = map => {
    setIsPostalCode(false);
    setIsProviderName(false);
    setAddress("");
    setSelectedClinicsByProvider("");
    const newCenter = map.map.center;
    const location = { lat: newCenter.lat(), lng: newCenter.lng() };
    setDragLocation(location);
  };

  const handleMapDragEnd = map => {
    setIsDragPin(true);
    const newCenter = map.map.center;
    const location = { lat: newCenter.lat(), lng: newCenter.lng() };
    setCurrentLocation(location);

    if (dragTimeout) {
      clearTimeout(dragTimeout);
    }
    const updatedDataClinics = {
      ...dataClinics,
      latitude: location.lat,
      longtitude: location.lng,
      address: null,
      providerName: null,
    };
    const timeout = setTimeout(() => {
      dispatch(searchClinics(updatedDataClinics));
    }, 1500);
    setDragTimeout(timeout);
  };

  const getMapCenter = () => {
    if (
      ((isPostalCode || isProviderName) && providerList.length === 1) ||
      (isProviderName && providerList.length > 1)
    ) {
      return {
        lat: providerList[0].latitude,
        lng: providerList[0].longitude,
      };
    }

    if (isPostalCode || isProviderName) {
      return {
        lat: searchClinicsList.latitude,
        lng: searchClinicsList.longitude,
      };
    }

    return null;
  };

  // eslint-disable-next-line
  const apiKey = "AIzaSyDQ3EoPPzvsYs8-pKAsK_Ksfz2JTYN0EzA"; /* NOSONAR */
  const markerIcon = {
    url: markerIconCustom,
    scaledSize: { width: 50, height: 50 },
  };

  function clinicIcons(selected = false) {
    return {
      url: `../../assets/images/clinic_${selected ? "green" : "red"}.svg`,
      scaledSize: { width: 40, height: 40 },
    };
  }

  const providerList = searchClinicsList.providerList || [];

  // for marker position
  const isSingleProvider = !isDragPin && (providerList.length === 1 || providerList.length > 1);
  const isSearchClinic = isPostalCode || isProviderName;
  
  let markerPosition;

  if (isDragPin) {
    markerPosition = dragLocation || currentLocation;
  } else if (isSingleProvider) {
    markerPosition = {
      lat: providerList[0].latitude,
      lng: providerList[0].longitude,
    };
  } else if (isSearchClinic) {
    markerPosition = {
      lat: searchClinicsList.latitude,
      lng: searchClinicsList.longitude,
    };
  } else {
    markerPosition = dragLocation || currentLocation;
  }

  return (
    <Card>
      <CardSection>
        <Fragment>
          <div className="claim-clinics-wrapper">
            <div className="claim-clinics-filter">
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  name="address"
                  value={address}
                  className="textfield textfield--boxed clinics-input"
                  placeholder={intl.formatMessage(messages.claims.clinicsAddressPlaceholder)}
                  onChange={handleAddressChange}
                  disabled={isFetching}
                />
                {!isFetching && address && (
                  <button type="button" className="button-clear" onClick={() => setAddress("")}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="button-address"
                  onClick={handleSubmitAddress}
                  disabled={isFetching || !address}
                >
                  {isFetching ? (
                    <i className={`fa fa-spinner`}></i>
                  ) : (
                    <i className={`fas fa-magnifying-glass`}></i>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="claim-clinics-wrapper">
            <div className="claim-clinics-filter">
              <div style={{ position: "relative" }}>
                <Select
                  value={clinicsByProviderTypeFilter.find(
                    filter => filter.value === selectedClinicsByProvider
                  )}
                  defaultValue={""}
                  onChange={handleProviderNameChange}
                  isClearable={true}
                  options={clinicsByProviderTypeFilter}
                  placeholder={intl.formatMessage(messages.claims.clinicsNamePlaceholder)}
                  disabled={isFetching}
                />
                {selectedClinicsByProvider && !isFetching && (
                  <button type="button" className="button-clear" onClick={handleProviderNameChange}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="claim-clinics-wrapper">
            <div className="claim-all-clinics-filter">
              <Select
                value={
                  selectedAllClinics
                    ? allClinicsFilter.find(filter => filter.value === selectedAllClinics)
                    : selectedEcards || allClinicsFilter[0]
                }
                onChange={handleAllClinicsChange}
                isClearable={false}
                options={allClinicsFilter}
                disabled={isFetching}
              />
            </div>
          </div>

          <ClickOutside>
            <div className="clinics-wrapper">
              <div style={{ position: "relative", width: "100%", height: "600px" }}>
                <div
                  style={{
                    opacity: isFetching ? 0.5 : 1,
                    pointerEvents: isFetching ? "none" : "auto",
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <APIProvider apiKey={apiKey}>
                    <Map
                      style={{ width: "auto", height: "600px" }}
                      defaultCenter={
                        (providerList.length === 1 && {
                          lat: providerList[0].latitude,
                          lng: providerList[0].longitude,
                        }) ||
                        currentLocation
                      }
                      center={getMapCenter()}
                      defaultZoom={17}
                      gestureHandling={"greedy"}
                      onDrag={map => {
                        handleMapDrag(map);
                      }}
                      onDragend={map => handleMapDragEnd(map)}
                    >
                      <Marker position={markerPosition} icon={markerIcon} zIndex={2} />

                      {providerList.length > 0 &&
                        providerList.map(provider => (
                          <Fragment key={provider.providerId}>
                            <Marker
                              position={{ lat: provider.latitude, lng: provider.longitude }}
                              icon={
                                activeMarker === (provider.address || provider.distance)
                                  ? clinicIcons(true)
                                  : clinicIcons(false)
                              }
                              title={provider.name}
                              onClick={() =>
                                setActiveMarker(
                                  provider.address ? provider.address : provider.distance
                                )
                              }
                              zIndex={1}
                            />
                            {activeMarker === (provider.address || provider.distance) && (
                              <InfoWindow
                                position={{ lat: provider.latitude, lng: provider.longitude }}
                                onCloseClick={() => setActiveMarker(null)}
                                pixelOffset={[0, -30]}
                              >
                                <div className="info-window-content">
                                  <p>{provider.name}</p>
                                  <span>{provider.address}</span>
                                </div>
                              </InfoWindow>
                            )}
                          </Fragment>
                        ))}
                    </Map>
                  </APIProvider>
                </div>
                {isFetching && (
                  <div className="loading-clinics-overlay">
                    <p>Loading...</p>
                  </div>
                )}
              </div>
            </div>
          </ClickOutside>
          <div className="clinics-list-wrapper">
            {!isFetching &&
              providerList.length > 0 &&
              providerList.map(provider => (
                <Fragment key={provider.id}>
                  <h4>
                    <strong>{provider.name}</strong>
                  </h4>
                  <p>{provider.address}</p>
                  <p>
                    <strong>
                      <img src={phone} alt="phone" width={15} /> {provider.contact}
                    </strong>
                  </p>
                  <p>{formatDistance(provider.distance)} KM</p>
                  {provider.service.split("\n").map(line => (
                    <Fragment key={line.trim()}>
                      {line}
                      <br />
                    </Fragment>
                  ))}
                </Fragment>
              ))}

            {!isFetching && isDragPin && providerList.length === 0 && (
              <div className="clinics-not-found">
                <h6>{intl.formatMessage(messages.claims.noClinicsFoundDescription)}</h6>
              </div>
            )}

            {!isFetching && isPostalCode && providerList.length === 0 && (
              <div className="clinics-not-found">
                <h6>
                  {intl.formatMessage(messages.claims.noClinicsFoundByPostalCodeDescription)}{" "}
                  {address}
                </h6>
              </div>
            )}

            {!isFetching && !isDragPin && !isPostalCode && providerList.length === 0 && <div></div>}
          </div>
        </Fragment>
      </CardSection>
    </Card>
  );
};
Clinics.propTypes = {
  intl: intlShape.isRequired,
};
export default injectIntl(Clinics);



// WEBPACK FOOTER //
// ./components/claim/ClinicsPage.js